import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Box, Paper, Typography, Badge, IconButton, Collapse, Stack,
    Chip, LinearProgress, Tooltip, Divider, Fab, ThemeProvider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../services/api';
import { useAuth } from './AuthProvider';
import lightTheme from '../themes/lightTheme';

const POLL_MS = 5000;

const STATUS = {
    queued:    { label: 'Na fila',          color: '#9e9e9e', icon: HourglassBottomIcon },
    running:   { label: 'Executando',       color: '#1976d2', icon: PlaylistPlayIcon },
    done:      { label: 'OK',               color: '#2e7d32', icon: CheckCircleIcon },
    partial:   { label: 'OK parcial',       color: '#ed6c02', icon: WarningAmberIcon },
    failed:    { label: 'Falhou',           color: '#aa2525', icon: ErrorIcon },
};

/**
 * Deriva o status visual da task. status="done" no DB pode esconder falhas
 * por nó (per_node[X].status="failed") quando a task termina mas algum SSH
 * falhou. Aqui:
 * - done com TODOS os nós ok → done (verde)
 * - done com pelo menos 1 nó failed e 1 nó ok → partial (laranja)
 * - done com TODOS failed → failed (vermelho)
 */
function effectiveStatus(task) {
    if (task.status !== 'done') return task.status;
    const perNode = task.progress?.per_node || {};
    const nodes = Object.values(perNode);
    if (nodes.length === 0) return 'done';
    const okCount = nodes.filter(n => n.status === 'ok').length;
    const failCount = nodes.filter(n => n.status === 'failed').length;
    if (failCount === 0) return 'done';
    if (okCount === 0) return 'failed';
    return 'partial';
}

const TYPE_LABEL = {
    dns_sync_blacklist:        'Sync blacklist',
    dns_sync_acl:              'Sync ACL',
    dns_sync_localzone:        'Sync zonas locais',
    dns_sync_node:             'Sync nó DNS',
    dns_validate_credentials:  'Valida SSH',
};

function fmtType(t) {
    return TYPE_LABEL[t] || t;
}

function fmtAge(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `${Math.floor(sec)}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    return `${Math.floor(sec / 3600)}h`;
}

/**
 * HUD flutuante (bottom-right) com fila de tasks DNS recentes.
 * Polla /tasks/active a cada 5s. Mostra contador de ativas, click expande
 * o painel listando running/queued + concluídas dos últimos 5min.
 */
export default function TaskQueueHUD() {
    const auth = useAuth();
    const [tasks, setTasks] = useState([]);
    const [open, setOpen] = useState(false);
    const [hidden, setHidden] = useState(false); // user fechou explicitamente
    const lastSeenIds = useRef(new Set());

    const fetchTasks = async () => {
        try {
            const res = await api.get('/tasks/active');
            const list = res.data?.data || [];
            // Detecta novas conclusões pra trazer atenção:
            const finishedNow = list.filter(t => (t.status === 'done' || t.status === 'failed')
                && !lastSeenIds.current.has(t.id));
            list.forEach(t => lastSeenIds.current.add(t.id));
            // Se algo concluiu, abre HUD por 1 ciclo (a menos que user tenha fechado)
            if (finishedNow.length > 0 && !hidden) setOpen(true);
            setTasks(list);
        } catch (e) {
            // 401/403 — usuário não logado. Para de pollar até reload.
            // Outros erros: ignora silenciosamente.
        }
    };

    useEffect(() => {
        if (!auth?.user) return;
        fetchTasks();
        const id = setInterval(fetchTasks, POLL_MS);
        // Listener: outros componentes disparam window.dispatchEvent(new Event('task-enqueued'))
        // após receber 202 com task_id, pra HUD atualizar instantâneo (sem esperar 5s).
        const onEnqueue = () => fetchTasks();
        window.addEventListener('task-enqueued', onEnqueue);
        return () => {
            clearInterval(id);
            window.removeEventListener('task-enqueued', onEnqueue);
        };
        // eslint-disable-next-line
    }, [auth?.user]);

    const counts = useMemo(() => {
        const effective = tasks.map(t => effectiveStatus(t));
        return {
            running: effective.filter(s => s === 'running').length,
            queued:  effective.filter(s => s === 'queued').length,
            failed:  effective.filter(s => s === 'failed').length,
            partial: effective.filter(s => s === 'partial').length,
            done:    effective.filter(s => s === 'done').length,
        };
    }, [tasks]);

    const activeCount = counts.running + counts.queued;
    const attentionCount = counts.failed + counts.partial;
    const showFab = !hidden && (tasks.length > 0);

    if (!showFab) return null;

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{
            position: 'fixed',
            bottom: 16, right: 16,
            zIndex: 1200,
            maxWidth: 380,
            width: open ? 380 : 'auto',
        }}>
            {!open ? (
                <Tooltip title={`${activeCount} ativa(s) · ${counts.failed} falha(s)`
                    + (counts.partial ? ` · ${counts.partial} parcial(is)` : '')
                    + ` · ${counts.done} OK`}>
                    <Badge
                        badgeContent={activeCount || attentionCount || null}
                        color={counts.failed > 0 ? 'error' : (counts.partial > 0 ? 'warning' : 'primary')}
                    >
                        <Fab
                            size="medium"
                            color={
                                counts.failed > 0 ? 'error'
                                    : counts.partial > 0 ? 'warning'
                                    : activeCount > 0 ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setOpen(true)}
                            sx={{ boxShadow: 4 }}
                        >
                            <PlaylistPlayIcon />
                        </Fab>
                    </Badge>
                </Tooltip>
            ) : (
                <Paper
                    elevation={6}
                    sx={{
                        p: 0,
                        borderRadius: 2,
                        overflow: 'hidden',
                        maxHeight: '60vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{
                        p: 1.5, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlaylistPlayIcon fontSize="small" />
                            <Typography variant="subtitle2">Fila de Tasks DNS</Typography>
                        </Box>
                        <Box>
                            <IconButton size="small" onClick={() => setOpen(false)} title="Minimizar">
                                <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setHidden(true)} title="Fechar">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ px: 1.5, py: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                        {counts.running > 0 && (
                            <Chip size="small" label={`${counts.running} executando`} color="info" variant="outlined" />
                        )}
                        {counts.queued > 0 && (
                            <Chip size="small" label={`${counts.queued} na fila`} color="default" variant="outlined" />
                        )}
                        {counts.done > 0 && (
                            <Chip size="small" label={`${counts.done} OK`} color="success" variant="outlined" />
                        )}
                        {counts.partial > 0 && (
                            <Chip size="small" label={`${counts.partial} parcial(is)`} color="warning" variant="outlined" />
                        )}
                        {counts.failed > 0 && (
                            <Chip size="small" label={`${counts.failed} falha`} color="error" variant="outlined" />
                        )}
                    </Box>

                    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        {tasks.length === 0 && (
                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography variant="body2">Nenhuma task ativa</Typography>
                            </Box>
                        )}
                        {tasks.map(t => <TaskRow key={t.id} task={t} />)}
                    </Box>
                </Paper>
            )}
        </Box>
        </ThemeProvider>
    );
}

function TaskRow({ task }) {
    const effStatus = effectiveStatus(task);
    const meta = STATUS[effStatus] || STATUS.queued;
    const Icon = meta.icon;

    // Progress per_node se houver
    const perNode = task.progress?.per_node || {};
    const nodeEntries = Object.entries(perNode);
    const phase = task.progress?.phase;
    const okNodes = nodeEntries.filter(([, info]) => info.status === 'ok').length;
    const failNodes = nodeEntries.filter(([, info]) => info.status === 'failed').length;

    return (
        <Box sx={{
            px: 1.5, py: 1,
            borderBottom: '1px solid', borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Icon sx={{ color: meta.color, fontSize: 20, mt: 0.25 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {fmtType(task.type)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {fmtAge(task.created_at)} atrás
                        </Typography>
                    </Box>

                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        #{task.id}
                        {task.username && ` · ${task.username}`}
                        {phase && ` · ${phase}`}
                    </Typography>

                    {/* Resumo de status quando há per_node — destaca a real situação */}
                    {nodeEntries.length > 0 && task.status === 'done' && (
                        <Typography variant="caption" sx={{
                            display: 'block', fontWeight: 600,
                            color: effStatus === 'done' ? 'success.main'
                                : effStatus === 'failed' ? 'error.main'
                                : 'warning.main',
                        }}>
                            {effStatus === 'done'    && `✓ ${okNodes}/${nodeEntries.length} servidor(es) sincronizado(s)`}
                            {effStatus === 'partial' && `⚠ ${okNodes} OK · ${failNodes} falhou — verifique abaixo`}
                            {effStatus === 'failed'  && `✗ Falhou em todos os ${failNodes} servidor(es)`}
                        </Typography>
                    )}

                    {task.status === 'running' && <LinearProgress sx={{ mt: 0.5, height: 3, borderRadius: 2 }} />}

                    {/* Per-node summary */}
                    {nodeEntries.length > 0 && (
                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {nodeEntries.map(([nid, info]) => {
                                const ok = info.status === 'ok';
                                return (
                                    <Tooltip key={nid} title={info.message || (ok ? 'OK' : 'Falhou')}>
                                        <Chip
                                            size="small"
                                            label={info.name}
                                            color={ok ? 'success' : 'error'}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: 10 }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    )}

                    {/* Erro */}
                    {task.status === 'failed' && task.error && (
                        <Typography variant="caption" sx={{ color: 'error.main', fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                            {String(task.error).slice(0, 200)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
